import React from 'react';
import { create } from 'zustand';
import { logger } from '../utils/logger';
import { persist } from 'zustand/middleware';
import { Notification, NotificationType, NotificationRelatedType } from '../types';
import { useAuthStore } from './authStore';

const MAX_DELETED_IDS = 200;

interface NotificationStore {
  notifications: Notification[];
  deletedNotificationIds: string[];
  isLoading: boolean;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'userId' | 'createdAt' | 'isRead'>) => void;
  markAsRead: (notificationId: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (notificationId: string) => void;
  clearAllNotifications: () => void;
  generateNotification: (
    type: NotificationType,
    title: string,
    message: string,
    relatedId?: string,
    relatedType?: NotificationRelatedType
  ) => void;

  // Notifications spécifiques
  notifyPostDue: (postName: string, postId: string) => void;
  notifyNewClient: (clientName: string, clientId: string) => void;
  notifyShootingReminder: (contractId: string, clientName: string, date: string) => void;

  // Utils
  getNotificationsByType: (type: NotificationType) => Notification[];
  getUnreadCount: () => number; // Getter dynamique au lieu d'une valeur statique
  cleanExpiredNotifications: () => void;

  // User session management
  clearUserNotifications: (userId: string) => void;
}

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set, get) => ({
      notifications: [],
      deletedNotificationIds: [],
      isLoading: false,

      addNotification: (notificationData) => {
        const { user } = useAuthStore.getState();
        if (!user) return;

        // Créer un ID stable basé sur le contenu pour éviter les doublons
        const stableId = notificationData.relatedId && notificationData.type 
          ? `${notificationData.type}_${notificationData.relatedId}_${user.id}`
          : `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        set(state => {
          // Vérifier si cette notification n'a pas été supprimée
          if (state.deletedNotificationIds.includes(stableId)) {
            logger.log('🚫 Notification ignorée (déjà supprimée):', notificationData.title);
            return state;
          }

          // Vérifier si cette notification existe déjà pour ce user
          const existingNotification = state.notifications.find(n => 
            n.id === stableId && n.userId === user.id
          );
          if (existingNotification) {
            logger.log('🔄 Notification existe déjà:', notificationData.title);
            return state;
          }

          const notification: Notification = {
            id: stableId,
            userId: user.id,
            isRead: false,
            createdAt: new Date().toISOString(),
            ...notificationData
          };

          const newNotifications = [notification, ...state.notifications];
          
          logger.log('📣 Nouvelle notification pour', user.email, ':', notification.title);
          return {
            ...state,
            notifications: newNotifications
          };
        });
      },

      markAsRead: (notificationId: string) => {
        const { user } = useAuthStore.getState();
        if (!user) return;

        set(state => {
          const updatedNotifications = state.notifications.map(notification =>
            notification.id === notificationId && notification.userId === user.id
              ? { ...notification, isRead: true }
              : notification
          );
          
          return {
            ...state,
            notifications: updatedNotifications
          };
        });
      },

      markAllAsRead: () => {
        const { user } = useAuthStore.getState();
        if (!user) return;

        set(state => ({
          ...state,
          notifications: state.notifications.map(notification => 
            notification.userId === user.id 
              ? { ...notification, isRead: true }
              : notification
          )
        }));
      },

      deleteNotification: (notificationId: string) => {
        const { user } = useAuthStore.getState();
        if (!user) return;

        set(state => {
          const filteredNotifications = state.notifications.filter(n => n.id !== notificationId);
          const updatedDeletedIds = [...state.deletedNotificationIds, notificationId];
          // Garder seulement les MAX_DELETED_IDS derniers IDs
          const trimmedDeletedIds = updatedDeletedIds.length > MAX_DELETED_IDS
            ? updatedDeletedIds.slice(updatedDeletedIds.length - MAX_DELETED_IDS)
            : updatedDeletedIds;

          return {
            ...state,
            notifications: filteredNotifications,
            deletedNotificationIds: trimmedDeletedIds
          };
        });
      },

      clearAllNotifications: () => {
        const { user } = useAuthStore.getState();
        if (!user) return;

        set(state => {
          const userNotifications = state.notifications.filter(n => n.userId === user.id);
          const otherNotifications = state.notifications.filter(n => n.userId !== user.id);
          const updatedDeletedIds = [
            ...state.deletedNotificationIds,
            ...userNotifications.map(n => n.id)
          ];
          // Garder seulement les MAX_DELETED_IDS derniers IDs
          const trimmedDeletedIds = updatedDeletedIds.length > MAX_DELETED_IDS
            ? updatedDeletedIds.slice(updatedDeletedIds.length - MAX_DELETED_IDS)
            : updatedDeletedIds;

          return {
            ...state,
            notifications: otherNotifications,
            deletedNotificationIds: trimmedDeletedIds
          };
        });
      },

      clearUserNotifications: (userId: string) => {
        set(state => ({
          ...state,
          notifications: state.notifications.filter(n => n.userId !== userId)
        }));
      },

      generateNotification: (type, title, message, relatedId, relatedType) => {
        get().addNotification({
          type,
          title,
          message,
          relatedId,
          relatedType
        });
      },

      // Notifications spécifiques
      notifyPostDue: (postName: string, postId: string) => {
        get().generateNotification(
          'POST_DUE',
          'Post en retard',
          `Le post "${postName}" devait être publié et est en retard`,
          postId,
          'instagram_post'
        );
      },

      notifyNewClient: (clientName: string, clientId: string) => {
        get().generateNotification(
          'NEW_CLIENT',
          'Nouveau client',
          `Le client "${clientName}" a été ajouté et nécessite votre attention`,
          clientId,
          'client'
        );
      },

      notifyShootingReminder: (contractId: string, clientName: string, date: string) => {
        get().generateNotification(
          'SHOOTING_REMINDER',
          'Rappel shooting',
          `Shooting programmé pour ${clientName} le ${new Date(date).toLocaleDateString('fr-FR')}`,
          contractId,
          'instagram_contract'
        );
      },

      // Utils
      getNotificationsByType: (type: NotificationType) => {
        const { user } = useAuthStore.getState();
        if (!user) return [];
        return get().notifications.filter(n => n.userId === user.id && n.type === type);
      },

      getUnreadCount: () => {
        const { user } = useAuthStore.getState();
        if (!user) return 0;
        return get().notifications.filter(n => n.userId === user.id && !n.isRead).length;
      },

      cleanExpiredNotifications: () => {
        const { user } = useAuthStore.getState();
        if (!user) return;

        const now = new Date().toISOString();
        set(state => {
          const activeNotifications = state.notifications.filter(notification => {
            if (!notification.expiresAt) return true;
            return notification.expiresAt > now;
          });
          
          return {
            ...state,
            notifications: activeNotifications
          };
        });
      }
    }),
    {
      name: 'nudge-notifications-global',
      partialize: (state) => ({
        deletedNotificationIds: state.deletedNotificationIds,
        notifications: state.notifications, // Persister toutes les notifications
      })
    }
  )
);

// Hook pour nettoyer automatiquement les notifications expirées
export const useNotificationCleanup = () => {
  const cleanExpiredNotifications = useNotificationStore(state => state.cleanExpiredNotifications);

  React.useEffect(() => {
    const interval = setInterval(() => {
      cleanExpiredNotifications();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [cleanExpiredNotifications]);
};